import { ApiError, request, waitForTask, WaitForTaskResult } from 'react-common'
import { createAsyncThunk, createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit'
import { AxiosError } from 'axios'
import parseISO from 'date-fns/parseISO'
import cloneDeep from 'lodash/cloneDeep'
import merge from 'lodash/merge'
import { v4 as uuid } from 'uuid'
import { ActivityRecord } from '@/api/models/activityRecord'
import {
  ActivityPartAxis,
  ActivityPartId,
  ActivityWorkflow,
  ActivityWorkflowAggregate,
  ActivityWorkflowFinish,
  ActivityWorkflowId,
  ActivityWorkflowUpdate,
  AggregateApi,
  ObjectiveWorkflow,
  ObjectiveWorkflowAggregate,
  ObjectiveWorkflowAggregateId,
  ObjectiveWorkflowId,
} from '@/api/models/aggregate'
import { Objective, ObjectiveApi, ObjectiveResourceForm, ResourceInitRequestModel } from '@/api/models/objective'
import { ObjectiveRecord } from '@/api/models/objectiveRecord'
import { StartProctoringRequestModel } from '@/api/models/proctoringSession'
import { RootState } from '@/store'
import { findAndSetAggregateToTree, findAndSetAggregateWorkflowToTree } from '@/store/listenerMiddlewares/utils'
import { FrontActivityWithAggregate } from '@/store/slices/aggregateSlice/types/FrontActivityWithAggregate.type'
import { hydrateMetaAndSort } from '@/store/slices/aggregateSlice/utils/hydrateMetaAndSort'
import { aggregatesApi } from '../api/aggregatesApi'
import { objectiveWorkflowApi } from '../api/objectiveWorkflowApi'
import { proctoringSessionApi } from '../api/proctoringSessionApi'

export type AggregateState = {
  current: ObjectiveWorkflowAggregate | null
  objective: Objective | null
  ltiToken: string | null
  ltiForm: Record<string, string> | null
  records: ObjectiveRecord[]
  activityRecords: ActivityRecord[]
  aggregates: ActivityWorkflowAggregate[]
  activeActivityPartAxis: ActivityPartAxis | null
  activitiesWithAggregates: FrontActivityWithAggregate | null
  resourceInitRequestParams: ResourceInitRequestModel | null
  proctoringRequestParams: StartProctoringRequestModel | null
  startActivityWorkflowResponse: ActivityWorkflowAggregate | null
  activityWithAggregateLoading: boolean
  activityWithAggregateLoadDate: Date | null
  activityWorkflowsLoading: boolean
  activityWithAggregateByObjectiveWorkflowChildrenLoading: boolean
}

export const fetchCurrentFromObjectiveAsync = createAsyncThunk<ObjectiveWorkflowAggregate, string>(
  'aggregate/fetchCurrentFromObjective',
  async (id, { rejectWithValue }) => {
    try {
      return await AggregateApi.getByObjective(id)
    } catch (err) {
      if (err instanceof AxiosError) {
        return rejectWithValue(err.response)
      } else if (err instanceof ApiError) {
        return rejectWithValue(err)
      }
      throw err
    }
  },
)

export const fetchObjectiveRecordsAsync = createAsyncThunk<ObjectiveRecord[], string>(
  'aggregate/fetchObjectiveRecords',
  async (id, { rejectWithValue }) => {
    try {
      return await AggregateApi.getRecords(id)
    } catch (err) {
      if (err instanceof AxiosError) {
        return rejectWithValue(err.response)
      } else if (err instanceof ApiError) {
        return rejectWithValue(err)
      }
      throw err
    }
  },
)

const cleanParameters = (parameters: Record<string, unknown>) => {
  if (!parameters) {
    return {}
  }

  return Object.keys(parameters).reduce(
    (acc, key) => {
      if (parameters[key] !== null) {
        acc[key] = parameters[key]
      }

      return acc
    },
    {} as Partial<typeof parameters>,
  )
}

export type FetchLtiActivityParameters = {
  objectiveWorkflowId: ObjectiveWorkflowId
  activityWorkflowIds: ActivityWorkflowId[]
  activityPartIds: ActivityPartId[]
  parameters: {
    workflowId?: string
  }
}

export const fetchLtiResourceInitRequestAsync = createAsyncThunk<ResourceInitRequestModel, FetchLtiActivityParameters>(
  'aggregate/fetchLtiResourceInitRequest',
  async (data, { rejectWithValue }) => {
    try {
      return await ObjectiveApi.getObjectiveWorkflowsResourceInitRequest(
        data.objectiveWorkflowId,
        data.activityWorkflowIds,
        cleanParameters(data.parameters),
        data.activityPartIds,
      )
    } catch (err) {
      if (err instanceof AxiosError) {
        return rejectWithValue(err.response)
      } else if (err instanceof ApiError) {
        return rejectWithValue(err)
      }
      throw err
    }
  },
)

export const fetchLtiFormAsync = createAsyncThunk<ObjectiveResourceForm, FetchLtiActivityParameters>(
  'aggregate/fetchLtiForm',
  async (payload, { rejectWithValue }) => {
    try {
      return await ObjectiveApi.getActivityResourceForm(
        payload.objectiveWorkflowId,
        payload.activityWorkflowIds,
        cleanParameters(payload.parameters),
      )
    } catch (err) {
      if (err instanceof AxiosError) {
        return rejectWithValue(err.response)
      } else if (err instanceof ApiError) {
        return rejectWithValue(err)
      }
      throw err
    }
  },
)

type StartObjectiveWorkflowParams = {
  objectiveWorkflowAggregateId: ObjectiveWorkflowAggregateId
  id: string
}

export const startObjectiveWorkflowAsync = createAsyncThunk<ObjectiveWorkflowAggregate, StartObjectiveWorkflowParams>(
  'aggregate/startObjectiveWorkflow',
  async ({ objectiveWorkflowAggregateId, id }, { rejectWithValue }) => {
    try {
      return await AggregateApi.startObjectiveWorkflow(objectiveWorkflowAggregateId, id)
    } catch (err) {
      if (err instanceof AxiosError) {
        return rejectWithValue(err.response)
      } else if (err instanceof ApiError) {
        return rejectWithValue(err)
      }
      throw err
    } finally {
      aggregatesApi.util.invalidateTags(['Aggregates'])
    }
  },
)

export const submitObjectiveWorkflowAsync = createAsyncThunk<WaitForTaskResult<ObjectiveWorkflow>, string>(
  'aggregate/submitObjectiveWorkflow',
  async (workflowId, { rejectWithValue }) => {
    try {
      await AggregateApi.submitObjectiveWorkflow(workflowId)
      return await waitForTask({
        task: () => AggregateApi.getWorkflow(workflowId),
        condition: (data?: ObjectiveWorkflow) => !!data?.objectiveRecordId,
        interval: 2000,
        duration: 60000,
        stopOnError: true,
        shouldStop: () => false,
      })
    } catch (err) {
      if (err instanceof AxiosError) {
        return rejectWithValue(err.response)
      } else if (err instanceof ApiError) {
        return rejectWithValue(err)
      }
      throw err
    } finally {
      aggregatesApi.util.invalidateTags(['Aggregates'])
    }
  },
)

export const startActivityWorkflowAsync = createAsyncThunk<ActivityWorkflowAggregate, ActivityWorkflow>(
  'aggregate/startActivityWorkflow',
  async (data, { rejectWithValue }) => {
    try {
      return await AggregateApi.startActivityWorkflow(data)
    } catch (err) {
      if (err instanceof AxiosError) {
        return rejectWithValue(err.response)
      } else if (err instanceof ApiError) {
        return rejectWithValue(err)
      }
      throw err
    } finally {
      aggregatesApi.util.invalidateTags(['Aggregates'])
    }
  },
)

export const finishActivityWorkflowAsync = createAsyncThunk<ActivityWorkflowAggregate, ActivityWorkflowFinish>(
  'aggregate/finishActivityWorkflow',
  async (data, { rejectWithValue }) => {
    try {
      return await AggregateApi.finishActivityWorkflow(data)
    } catch (err) {
      if (err instanceof AxiosError) {
        return rejectWithValue(err.response)
      } else if (err instanceof ApiError) {
        return rejectWithValue(err)
      }
      throw err
    } finally {
      aggregatesApi.util.invalidateTags(['Aggregates'])
    }
  },
)

export const updateActivityWorkflowAsync = createAsyncThunk<
  ActivityWorkflowAggregate,
  {
    objectiveWorkflowId: ObjectiveWorkflowId
    payload: ActivityWorkflowUpdate
  }
>('aggregate/updateActivityWorkflow', async ({ objectiveWorkflowId, payload }, { rejectWithValue }) => {
  try {
    return await AggregateApi.updateActivityWorkflow(objectiveWorkflowId, payload)
  } catch (err) {
    if (err instanceof AxiosError) {
      return rejectWithValue(err.response)
    } else if (err instanceof ApiError) {
      return rejectWithValue(err)
    }
    throw err
  } finally {
    aggregatesApi.util.invalidateTags(['Aggregates'])
  }
})

export const fetchMyAggregatesAsync = createAsyncThunk<
  ActivityWorkflowAggregate[],
  {
    objectiveWorkflowId: ObjectiveWorkflowId
    queries: Record<string, any>
  }
>('fetchMyAggregatesByQuerysAsync/fetch', async ({ queries, objectiveWorkflowId }, { rejectWithValue }) => {
  try {
    return await AggregateApi.myAggregatesByQuery({ queries, objectiveWorkflowId })
  } catch (err) {
    if (err instanceof AxiosError) {
      return rejectWithValue(err.response)
    } else if (err instanceof ApiError) {
      return rejectWithValue(err)
    }
    throw err
  }
})

const finishObjectiveWorkflowRequest = request('finish-objective-workflows')

export const finishObjectiveWorkflowAsync = createAsyncThunk<ObjectiveWorkflowAggregate, string>(
  'aggregate/finishObjectiveWorkflow',
  async (objectiveWorkflowId, { rejectWithValue }) => {
    try {
      const response = await finishObjectiveWorkflowRequest.post({
        body: { id: uuid(), objectiveWorkflowId },
        headers: { prefer: 'respond-sync' },
      })
      return response.body
    } catch (err) {
      if (err instanceof AxiosError) {
        return rejectWithValue(err.response)
      } else if (err instanceof ApiError) {
        return rejectWithValue(err)
      }
      throw err
    } finally {
      aggregatesApi.util.invalidateTags(['Aggregates'])
    }
  },
)

const initialState: AggregateState = {
  current: null,
  objective: null,
  ltiToken: null,
  ltiForm: null,
  records: [],
  activityRecords: [],
  aggregates: [],
  activitiesWithAggregates: null,
  activeActivityPartAxis: null,
  resourceInitRequestParams: null,
  proctoringRequestParams: null,
  startActivityWorkflowResponse: null,
  activityWithAggregateLoading: true,
  activityWithAggregateLoadDate: null,
  activityWorkflowsLoading: false,
  activityWithAggregateByObjectiveWorkflowChildrenLoading: false,
}

export const aggregateSlice = createSlice({
  name: 'aggregate',
  initialState,
  reducers: {
    reset(state) {
      state.current = null
      state.objective = null
      state.ltiToken = null
      state.ltiForm = null
      state.resourceInitRequestParams = null
      state.records = []
      state.activityRecords = []
      state.aggregates = []
      state.activitiesWithAggregates = null
      state.activeActivityPartAxis = null
      state.startActivityWorkflowResponse = null
      state.activityWithAggregateLoading = false
      state.activityWorkflowsLoading = false
      state.activityWithAggregateByObjectiveWorkflowChildrenLoading = false
    },
    resetLtiSource(state) {
      state.ltiToken = null
      state.ltiForm = null
      state.resourceInitRequestParams = null
    },
    setActiveActivityPartAxis(state, action: PayloadAction<ActivityPartAxis>) {
      state.activeActivityPartAxis = action.payload
    },
    resetStartActivityWorkflowResponse(state) {
      state.startActivityWorkflowResponse = null
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCurrentFromObjectiveAsync.fulfilled, (state, action) => {
        state.current = action.payload
      })
      .addCase(startObjectiveWorkflowAsync.fulfilled, (state, action) => {
        state.current = action.payload
      })
      .addCase(fetchObjectiveRecordsAsync.fulfilled, (state, action) => {
        state.records = action.payload.sort((a, b) => parseISO(b.finished).valueOf() - parseISO(a.finished).valueOf()) // TODO can be simplified
      })
      .addCase(fetchLtiResourceInitRequestAsync.fulfilled, (state, action) => {
        state.resourceInitRequestParams = action.payload
      })
      .addCase(fetchLtiFormAsync.fulfilled, (state, action) => {
        state.ltiForm = action.payload.form
      })
      .addCase(submitObjectiveWorkflowAsync.fulfilled, (state, action) => {
        if (state.current) {
          state.current.lastObjectiveWorkflow = action.payload.result ?? null
        }
      })
      .addCase(fetchMyAggregatesAsync.pending, (state) => {
        state.activityWorkflowsLoading = true
      })
      .addCase(fetchMyAggregatesAsync.fulfilled, (state, action) => {
        state.activityWorkflowsLoading = false
        state.aggregates = action.payload
      })
      .addCase(fetchMyAggregatesAsync.rejected, (state) => {
        state.activityWorkflowsLoading = false
      })
      .addCase(startActivityWorkflowAsync.fulfilled, (state, action) => {
        if (!state.activitiesWithAggregates) return

        state.activitiesWithAggregates = findAndSetAggregateWorkflowToTree(
          state.activitiesWithAggregates,
          action.payload,
        )
      })
      .addCase(finishActivityWorkflowAsync.fulfilled, (state, action) => {
        if (!state.activitiesWithAggregates) return

        state.activitiesWithAggregates = findAndSetAggregateWorkflowToTree(
          state.activitiesWithAggregates,
          action.payload,
        )
      })
      .addCase(finishObjectiveWorkflowAsync.fulfilled, (state, action) => {
        state.current = action.payload
      })
      .addMatcher(
        objectiveWorkflowApi.endpoints.fetchActivityWorkflowAggregateFullTree.matchFulfilled,
        (state, action) => {
          const payload = action.payload
          const stateActivityWithAggregates = cloneDeep(state.activitiesWithAggregates)

          const updatedActivityWithAggregates = merge(stateActivityWithAggregates, payload)

          state.activitiesWithAggregates = hydrateMetaAndSort(updatedActivityWithAggregates)

          if (!state.activitiesWithAggregates) return

          state.activityWithAggregateLoading = false
          state.activityWithAggregateLoadDate = new Date()
        },
      )
      .addMatcher(
        objectiveWorkflowApi.endpoints.fetchActivityWithAggregateByObjectiveWorkflowAggregate.matchFulfilled,
        (state, action) => {
          const args = action.meta.arg.originalArgs

          if (args.standalone) return

          if (!state.activitiesWithAggregates) {
            state.activitiesWithAggregates = hydrateMetaAndSort(action.payload)
            return
          }

          state.activitiesWithAggregates = findAndSetAggregateToTree(state.activitiesWithAggregates, action.payload)
        },
      )
      .addMatcher(
        objectiveWorkflowApi.endpoints.fetchActivityWithAggregateByObjectiveWorkflowChildren.matchPending,
        (state) => {
          state.activityWithAggregateByObjectiveWorkflowChildrenLoading = true
        },
      )
      .addMatcher(
        objectiveWorkflowApi.endpoints.fetchActivityWithAggregateByObjectiveWorkflowChildren.matchRejected,
        (state) => {
          state.activityWithAggregateByObjectiveWorkflowChildrenLoading = false
        },
      )
      .addMatcher(
        objectiveWorkflowApi.endpoints.fetchActivityWithAggregateByObjectiveWorkflowChildren.matchFulfilled,
        (state, action) => {
          state.activityWithAggregateLoading = false
          state.activityWithAggregateByObjectiveWorkflowChildrenLoading = false

          if (!state.activitiesWithAggregates) {
            throw Error('Could not call this method without existing stage')
          }

          action.payload.forEach((activityWithAggregate) => {
            state.activitiesWithAggregates = findAndSetAggregateToTree(
              state.activitiesWithAggregates!,
              activityWithAggregate,
            )
          })
        },
      )
      .addMatcher(objectiveWorkflowApi.endpoints.fetchActivityRecords.matchFulfilled, (state, action) => {
        state.activityRecords = action.payload
      })
      .addMatcher(objectiveWorkflowApi.endpoints.fetchActivityRecords.matchPending, (state) => {
        state.activityRecords = []
      })
      .addMatcher(objectiveWorkflowApi.endpoints.fetchActivityRecords.matchRejected, (state) => {
        state.activityRecords = []
      })
      .addMatcher(aggregatesApi.endpoints.getObjectiveAccessById.matchFulfilled, (state, action) => {
        state.objective = action.payload.objective

        if (state.current) {
          state.current.objective = action.payload.objective
        }
      })
      .addMatcher(proctoringSessionApi.endpoints.getProctoringStartToken.matchFulfilled, (state, action) => {
        state.proctoringRequestParams = action.payload
      })
      .addMatcher(proctoringSessionApi.endpoints.getProctoringStartToken.matchPending, (state) => {
        state.proctoringRequestParams = null
      })
      .addMatcher(proctoringSessionApi.endpoints.getProctoringStartToken.matchRejected, (state) => {
        state.proctoringRequestParams = null
      })
  },
})

const rootSelector = (state: RootState) => state.aggregate
export const activitySupportMobileViewSelector = createSelector(
  rootSelector,
  (aggregateState) => aggregateState.objective?.activity?.supportMobileView,
)

export const {
  reset,
  resetLtiSource: resetLtiSourceAction,
  setActiveActivityPartAxis,
  resetStartActivityWorkflowResponse,
} = aggregateSlice.actions

export default aggregateSlice.reducer
